/**
 * Admin — Job postings management.
 * Super-admin / admin can create, edit, toggle and delete job openings.
 * Every change goes live on /careers immediately.
 */
import React, { useEffect, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  Briefcase, Plus, PencilSimple, Trash, X, Eye, EyeSlash,
} from "@phosphor-icons/react";

const EMPLOYMENT_TYPES = [
  { v: "full_time", l: "Full-time" },
  { v: "part_time", l: "Part-time" },
  { v: "internship", l: "Internship" },
  { v: "contract", l: "Contract" },
  { v: "freelance", l: "Freelance" },
];
const EXPERIENCE_LEVELS = [
  { v: "entry", l: "Entry level" },
  { v: "mid", l: "Mid level" },
  { v: "senior", l: "Senior" },
  { v: "lead", l: "Lead" },
  { v: "any", l: "Any" },
];

const emptyForm = () => ({
  title: "", department: "Operations", location: "",
  employment_type: "full_time", experience_level: "mid",
  summary: "", description: "",
  responsibilities: "", requirements: "", compensation_range: "",
  active: true,
});

export default function AdminCareers() {
  const [items, setItems] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/careers");
      setItems(r.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm());
    setModalOpen(true);
  };
  const openEdit = (j) => {
    setEditing(j);
    setForm({
      title: j.title, department: j.department, location: j.location,
      employment_type: j.employment_type, experience_level: j.experience_level,
      summary: j.summary || "", description: j.description || "",
      responsibilities: (j.responsibilities || []).join("\n"),
      requirements: (j.requirements || []).join("\n"),
      compensation_range: j.compensation_range || "",
      active: j.active,
    });
    setModalOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.location.trim()) {
      toast.error("Title and location are required"); return;
    }
    setBusy(true);
    const payload = {
      ...form,
      responsibilities: form.responsibilities.split("\n").map(s => s.trim()).filter(Boolean),
      requirements: form.requirements.split("\n").map(s => s.trim()).filter(Boolean),
    };
    try {
      if (editing) {
        await api.put(`/admin/careers/${editing.id}`, payload);
        toast.success("Job updated");
      } else {
        await api.post("/admin/careers", payload);
        toast.success("Job created");
      }
      setModalOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const remove = async (j) => {
    if (!window.confirm(`Delete "${j.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/careers/${j.id}`);
      toast.success("Deleted"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggle = async (j) => {
    try {
      await api.post(`/admin/careers/${j.id}/toggle`);
      toast.success(j.active ? "Hidden from careers page" : "Now visible on careers page");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AppShell title="Job Postings"
              subtitle="Publish, edit and retire roles shown on /careers"
              actions={
                <button onClick={openNew} data-testid="btn-new-job"
                        className="inline-flex items-center gap-2 h-10 px-4 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium">
                  <Plus size={14} weight="bold"/>New posting
                </button>
              }>
      <div className="bg-white border border-[#E5E2D9] rounded-md overflow-x-auto">
        <table className="w-full text-sm" data-testid="jobs-table">
          <thead>
            <tr className="bg-[#F7F6F2] text-left text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">
              <th className="px-6 py-3">Title</th>
              <th className="px-6 py-3">Department</th>
              <th className="px-6 py-3">Location</th>
              <th className="px-6 py-3">Type</th>
              <th className="px-6 py-3">Status</th>
              <th className="px-6 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={6} className="px-6 py-16 text-center text-[#8A8780]">
                <Briefcase size={28} weight="duotone" color="#8A8780" className="mx-auto mb-3"/>
                No job postings yet. Click "New posting" to create your first one.
              </td></tr>
            )}
            {items.map((j, i) => (
              <tr key={j.id} className={i % 2 === 0 ? "bg-white" : "bg-[#FAFAF7]"} data-testid={`job-row-${j.id}`}>
                <td className="px-6 py-3">
                  <div className="font-medium">{j.title}</div>
                  {j.summary && <div className="text-xs text-[#8A8780] mt-0.5 line-clamp-1">{j.summary}</div>}
                </td>
                <td className="px-6 py-3 text-xs">{j.department}</td>
                <td className="px-6 py-3 text-xs">{j.location}</td>
                <td className="px-6 py-3 text-xs">{EMPLOYMENT_TYPES.find(t => t.v === j.employment_type)?.l || j.employment_type}</td>
                <td className="px-6 py-3">
                  <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] rounded ${j.active ? "bg-[#DCEDD3] text-[#2D5A27]" : "bg-[#E5E2D9] text-[#5C5A55]"}`}>
                    {j.active ? "Active" : "Hidden"}
                  </span>
                </td>
                <td className="px-6 py-3 text-right">
                  <button onClick={() => toggle(j)}
                          data-testid={`job-toggle-${j.id}`}
                          className="text-[#5C5A55] hover:text-[#1A1A1A] text-xs mr-3 inline-flex items-center gap-1">
                    {j.active ? <><EyeSlash size={12}/>Hide</> : <><Eye size={12}/>Show</>}
                  </button>
                  <button onClick={() => openEdit(j)}
                          data-testid={`job-edit-${j.id}`}
                          className="text-[#2D5A27] text-xs mr-3 inline-flex items-center gap-1">
                    <PencilSimple size={12}/>Edit
                  </button>
                  <button onClick={() => remove(j)}
                          data-testid={`job-del-${j.id}`}
                          className="text-[#C25D38] text-xs inline-flex items-center gap-1">
                    <Trash size={12}/>Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto"
             onClick={() => setModalOpen(false)}>
          <div onClick={e => e.stopPropagation()}
               data-testid="job-form-modal"
               className="bg-white rounded-md w-full max-w-3xl my-8 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-[#E5E2D9] flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">
                  {editing ? "Edit posting" : "New posting"}
                </div>
                <div className="font-heading text-lg mt-1">
                  {editing ? editing.title : "Create a new job opening"}
                </div>
              </div>
              <button onClick={() => setModalOpen(false)} className="p-1 hover:bg-[#F7F6F2] rounded">
                <X size={16}/>
              </button>
            </div>

            <form onSubmit={submit} className="p-6 space-y-5">
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Title" required value={form.title}
                       onChange={v => setForm(f => ({ ...f, title: v }))}
                       testid="jf-title"/>
                <Field label="Department" required value={form.department}
                       onChange={v => setForm(f => ({ ...f, department: v }))}
                       testid="jf-department"/>
                <Field label="Location" required value={form.location}
                       onChange={v => setForm(f => ({ ...f, location: v }))}
                       testid="jf-location"/>
                <Field label="Compensation range" value={form.compensation_range}
                       onChange={v => setForm(f => ({ ...f, compensation_range: v }))}
                       placeholder="₹ 15-24 LPA + ESOPs"
                       testid="jf-comp"/>
                <Select label="Employment type" value={form.employment_type}
                        options={EMPLOYMENT_TYPES}
                        onChange={v => setForm(f => ({ ...f, employment_type: v }))}
                        testid="jf-emptype"/>
                <Select label="Experience level" value={form.experience_level}
                        options={EXPERIENCE_LEVELS}
                        onChange={v => setForm(f => ({ ...f, experience_level: v }))}
                        testid="jf-explevel"/>
              </div>

              <TextArea label="One-line summary (shown on job card)" value={form.summary}
                         rows={2} max={280}
                         onChange={v => setForm(f => ({ ...f, summary: v }))}
                         testid="jf-summary"/>
              <TextArea label="Full description" value={form.description}
                         rows={5}
                         onChange={v => setForm(f => ({ ...f, description: v }))}
                         testid="jf-desc"/>
              <TextArea label="Responsibilities · one per line" value={form.responsibilities}
                         rows={5}
                         onChange={v => setForm(f => ({ ...f, responsibilities: v }))}
                         testid="jf-resp"/>
              <TextArea label="Requirements · one per line" value={form.requirements}
                         rows={5}
                         onChange={v => setForm(f => ({ ...f, requirements: v }))}
                         testid="jf-req"/>

              <label className="inline-flex items-center gap-2 text-sm text-[#5C5A55]">
                <input type="checkbox" checked={form.active}
                       onChange={e => setForm(f => ({ ...f, active: e.target.checked }))}
                       data-testid="jf-active"/>
                Publish immediately (show on /careers)
              </label>

              <div className="flex justify-between pt-4 border-t border-[#E5E2D9]">
                <a href="/careers" target="_blank" rel="noreferrer"
                   className="text-xs text-[#2D5A27] underline">
                  Preview /careers →
                </a>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setModalOpen(false)}
                          className="h-10 px-4 border border-[#E5E2D9] rounded-md text-sm">Cancel</button>
                  <button type="submit" disabled={busy} data-testid="jf-submit"
                          className="h-10 px-4 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium disabled:opacity-50">
                    {busy ? "Saving…" : editing ? "Save changes" : "Create posting"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </AppShell>
  );
}

function Field({ label, value, onChange, required, type = "text", testid, placeholder }) {
  return (
    <label className="block">
      <span className="block text-xs text-[#5C5A55] mb-1">{label}{required && " *"}</span>
      <input type={type} value={value} placeholder={placeholder}
             onChange={e => onChange(e.target.value)} required={required}
             data-testid={testid}
             className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md bg-white text-sm focus:border-[#2D5A27] outline-none"/>
    </label>
  );
}
function Select({ label, value, options, onChange, testid }) {
  return (
    <label className="block">
      <span className="block text-xs text-[#5C5A55] mb-1">{label}</span>
      <select value={value} onChange={e => onChange(e.target.value)}
              data-testid={testid}
              className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md bg-white text-sm">
        {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </label>
  );
}
function TextArea({ label, value, onChange, rows = 4, max, testid }) {
  return (
    <label className="block">
      <span className="block text-xs text-[#5C5A55] mb-1">
        {label}{max && <span className="text-[#8A8780]"> · {value.length}/{max}</span>}
      </span>
      <textarea rows={rows} value={value} maxLength={max}
                onChange={e => onChange(e.target.value)}
                data-testid={testid}
                className="w-full px-3 py-2 border border-[#E5E2D9] rounded-md bg-white text-sm focus:border-[#2D5A27] outline-none"/>
    </label>
  );
}
